import {
  resolveScenarioCreateDataSource,
  slugFromDisplayName,
} from './vectorScenarioRegistry';

/** User-facing strategy categories on the submission form. */
export const SUBMISSION_STRATEGY_KINDS = [
  {
    id: 'equity_curve',
    label: 'Equity Curve Strategy (Stocks)',
    blurb: 'Daily equity curve (like Kairos) for a single ticker or blended book. Upload a CSV baseline first, or connect TradingView daily equity webhooks if you skip the CSV.',
    category: 'stocks',
  },
  {
    id: 'stocks',
    label: 'Trading View Strategy (Stocks)',
    blurb: 'Stock or ETF strategy with live TradingView order alerts. Upload a TradingView List of trades CSV first when you have one, or submit webhook-only and add history later.',
    category: 'stocks',
  },
  {
    id: 'futures',
    label: 'Futures Strategy',
    blurb: 'Futures strategy backed by a TradesViz trade journal CSV and/or NinjaTrader Trade Alert webhooks.',
    category: 'futures',
  },
  {
    id: 'stock_trades',
    label: 'Stock trades (each buy & sell)',
    blurb: 'Your system POSTs every buy and sell with its real symbol, quantity, price and time. RiskLo pairs them into closed trades and tracks open positions.',
    category: 'stocks',
  },
];

export const WEBHOOK_MAPPING_NOTE = (
  'If the webhook has not fired yet, an admin will need to map it to this strategy '
  + 'in Webhooks after the first alert arrives.'
);

export const STRATEGY_KIND_EXAMPLES = {
  equity_curve: {
    title: 'Equity Curve Strategy (Stocks) — what to upload',
    sections: [
      {
        heading: 'Option 1 — CSV (recommended)',
        body: 'Daily equity curve (like Kairos) — one row per day with a date column and an equity, value, balance, or NAV column. When you open the file in Excel or Google Sheets it looks like this:',
        spreadsheet: {
          caption: 'Example spreadsheet — export or save as .csv to upload',
          columns: ['date', 'equity'],
          rows: [
            ['2024-01-02', '100,000.00'],
            ['2024-01-03', '100,450.00'],
            ['2024-01-04', '99,800.00'],
            ['2024-01-05', '101,120.00'],
          ],
        },
      },
      {
        heading: 'Option 2 — TradingView webhook (if you skip CSV)',
        body: 'Daily equity snapshots posted from TradingView / Kairos (same style as Kairos Select or Kairos Enhanced). A CSV baseline is strongly recommended so the curve has history on day one.',
        code: `POST JSON:
{
  "strategy_id": "QLD",
  "date": "2024-06-01",
  "equity": 102340.50
}`,
      },
    ],
  },
  stocks: {
    title: 'Trading View Strategy (Stocks) — what to upload',
    sections: [
      {
        heading: 'Option 1 — CSV (recommended)',
        body: 'TradingView strategy report — export the List of trades tab as CSV (one row per entry/exit). We convert it into an equity curve. When you open the file in Excel or Google Sheets it looks like this:',
        spreadsheet: {
          caption: 'Example TradingView List of trades export — save as .csv to upload',
          wide: true,
          columns: [
            'Trade number',
            'Type',
            'Date and time',
            'Signal',
            'Price USD',
            'Size (qty)',
            'Net PnL USD',
            'Cumulative PnL USD',
          ],
          rows: [
            ['1', 'Entry long', '2022-01-03 20:30', 'Long', '400.17', '234', '', ''],
            ['1', 'Exit long', '2022-01-04 14:45', 'Exit Long', '412.50', '234', '2,881.62', '2,881.62'],
            ['2', 'Entry short', '2022-01-05 09:15', 'Short', '415.20', '240', '', '2,881.62'],
            ['2', 'Exit short', '2022-01-05 15:30', 'Exit Short', '408.75', '240', '1,548.00', '4,429.62'],
            ['3', 'Entry long', '2022-01-06 10:00', 'Long', '410.80', '243', '', '4,429.62'],
            ['3', 'Exit long', '2022-01-07 11:20', 'Exit Long', '418.35', '243', '1,834.65', '6,264.27'],
          ],
        },
      },
      {
        heading: 'Option 2 — TradingView webhook (if you skip CSV)',
        body: 'Live strategy order alerts — entries and exits arrive as separate POSTs.',
        code: `POST JSON (example fields):
{
  "strategy_id": "my_stock_strat",
  "action": "buy",
  "executed_price": 412.50,
  "timestamp": "2024-06-01T15:30:00Z"
}`,
      },
    ],
  },
  futures: {
    title: 'Futures Strategy — what to upload',
    sections: [
      {
        heading: 'Option 1 — TradesViz CSV (recommended)',
        body: 'Trade journal export with trade dates, symbol, side, prices, and net P&L. When you open the file in Excel or Google Sheets it looks like this:',
        spreadsheet: {
          caption: 'Example TradesViz export — save as .csv to upload',
          wide: true,
          columns: ['close_date', 'symbol', 'side', 'qty', 'entry_price', 'exit_price', 'base_net_pnl'],
          rows: [
            ['2024-06-03', 'NQ', 'long', '1', '18,250.00', '18,310.00', '1,200.00'],
            ['2024-06-04', 'NQ', 'short', '1', '18,300.00', '18,240.00', '960.00'],
            ['2024-06-05', 'ES', 'long', '2', '5,280.50', '5,295.25', '1,475.00'],
          ],
        },
      },
      {
        heading: 'Option 2 — NinjaTrader Trade Alert webhook (if you skip CSV)',
        body: 'Pick an NT8 strategy name if it already exists in the dropdown. Alerts arrive as POSTs from your NinjaTrader Trade Alert setup.',
        code: `POST JSON:
{
  "username": "Trade Alert",
  "content": "… NT8 alert text …"
}`,
      },
    ],
  },
};

export const SUBMISSION_STEPS = [
  { id: 'brand', label: 'Brand' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'kind', label: 'Strategy type' },
  { id: 'baseline', label: 'Data & webhook' },
  { id: 'review', label: 'Review' },
];

export function dataNameFromDisplayName(name) {
  return String(name || '').trim().replace(/\s+/g, '_');
}

export function buildNt8AlertDataSourceConfig(displayName, nt8StrategyName) {
  const nt8 = String(nt8StrategyName || '').trim();
  return {
    strategyName: displayName.trim() || nt8,
    ...(nt8 ? { nt8StrategyName: nt8 } : {}),
  };
}

export function normalizeDisplaySymbol(value) {
  return String(value || '').trim().toUpperCase();
}

export function instrumentClassForStrategyKind(strategyKind) {
  return strategyKind === 'futures' ? 'futures' : 'stock';
}

export function strategySymbolFieldMeta(strategyKind) {
  if (strategyKind === 'futures') {
    return {
      label: 'Futures symbol (required)',
      placeholder: 'e.g. ES, NQ, MNQ',
      hint: 'Contract symbol used in Strategy Data and results (e.g. ES, NQ).',
    };
  }
  return {
    label: 'Stock symbol (required)',
    placeholder: 'e.g. TSLA, AAPL, QLD',
    hint: 'Ticker symbol used in Strategy Data and results.',
  };
}

export function strategyInstrumentConfig(displaySymbol, strategyKind) {
  const normalized = normalizeDisplaySymbol(displaySymbol);
  return {
    instrumentClass: instrumentClassForStrategyKind(strategyKind),
    displaySymbol: normalized,
  };
}

export function withStrategyInstrument(config, displaySymbol, strategyKind) {
  const normalized = normalizeDisplaySymbol(displaySymbol);
  if (!normalized) return config;
  return {
    ...config,
    ...strategyInstrumentConfig(normalized, strategyKind),
  };
}

/** Map UI strategy kind + CSV presence to the internal feed type the API expects. */
export function resolveSubmissionFeedType(strategyKind, { hasCsv } = {}) {
  const kind = String(strategyKind || '').trim();
  // Individual trades come only from the webhook (no CSV for this type yet).
  if (kind === 'stock_trades') return 'webhook_trade_fills';
  if (hasCsv) {
    if (kind === 'futures') return 'trade_journal';
    return 'equity_curve';
  }
  if (kind === 'equity_curve') return 'webhook_kairos';
  if (kind === 'stocks') return 'webhook_tradingview';
  if (kind === 'futures') return 'webhook_nt8_alert';
  return 'equity_curve';
}

export function strategyKindLabel(strategyKind) {
  return SUBMISSION_STRATEGY_KINDS.find((k) => k.id === strategyKind)?.label || strategyKind;
}

export function buildSubmissionPayload({
  entitySlug,
  displayName,
  slug,
  strategyName,
  strategyKind,
  visibility,
  description,
  submitterNotes,
  kairosSymbol,
  webhookStrategyId,
  nt8StrategyName,
  csvText,
  csvFileName,
  equityColumn,
  buildDataSourceConfig,
}) {
  const hasCsv = Boolean(String(csvText || '').trim());
  const feedType = resolveSubmissionFeedType(strategyKind, { hasCsv });
  const kindMeta = SUBMISSION_STRATEGY_KINDS.find((k) => k.id === strategyKind) || SUBMISSION_STRATEGY_KINDS[0];
  const resolved = resolveScenarioCreateDataSource(feedType, buildDataSourceConfig());
  return {
    entitySlug,
    displayName: displayName.trim(),
    slug: slug.trim(),
    strategyName: strategyName.trim(),
    category: kindMeta.category,
    dataSourceType: resolved.dataSourceType,
    visibility,
    description: description.trim() || null,
    submitterNotes: submitterNotes.trim() || null,
    dataSourceConfig: resolved.dataSourceConfig,
    csv: hasCsv ? csvText.trim() : undefined,
    csvFilename: csvFileName || undefined,
    equityColumn: equityColumn.trim() || undefined,
  };
}

export { slugFromDisplayName };
